// src/fileutils/threadCleanup.js
import FileReferenceHelper from './FileReferenceHelper';

/**
 * Clean up file references when a thread is deleted
 * This function should be called whenever a thread is deleted
 * 
 * @param {string} threadId - ID of the thread being deleted
 * @returns {Promise<Object>} - Summary of cleanup operation
 */
export async function cleanupThreadFileReferences(threadId) {
  if (!threadId) {
    console.error('Thread ID is required for file reference cleanup');
    return { error: 'Thread ID is required', deleted: 0, retained: 0 };
  }
  
  try {
    console.log(`Starting file reference cleanup for thread: ${threadId}`);
    
    const fileHelper = new FileReferenceHelper();
    const result = await fileHelper.cleanupFileReferencesForThread(threadId);
    
    console.log(`File reference cleanup complete for thread ${threadId}:`, result);
    return result;
  } catch (error) {
    console.error(`Error during file reference cleanup for thread ${threadId}:`, error);
    return { 
      error: error.message || 'Unknown error during file reference cleanup', 
      deleted: 0, 
      retained: 0 
    };
  }
}

/**
 * Batch cleanup of file references for multiple threads
 * Useful when multiple threads are deleted at once
 * 
 * @param {Array<string>} threadIds - Array of thread IDs to clean up
 * @returns {Promise<Object>} - Summary of all cleanup operations
 */
export async function batchCleanupThreadFileReferences(threadIds) {
  if (!threadIds || !Array.isArray(threadIds) || threadIds.length === 0) {
    return { error: 'No thread IDs provided for cleanup', deleted: 0, retained: 0 };
  }
  
  try {
    console.log(`Starting batch file reference cleanup for ${threadIds.length} threads`);
    
    let totalDeleted = 0;
    let totalRetained = 0;
    const errors = [];
    
    // Process each thread sequentially to avoid database conflicts
    for (const threadId of threadIds) {
      try {
        const result = await cleanupThreadFileReferences(threadId);
        totalDeleted += result.deleted || 0;
        totalRetained += result.retained || 0;
      } catch (err) {
        console.error(`Error cleaning up thread ${threadId}:`, err);
        errors.push({ threadId, error: err.message || 'Unknown error' });
      }
    }
    
    return {
      deleted: totalDeleted,
      retained: totalRetained,
      errors: errors.length > 0 ? errors : undefined
    };
  } catch (error) {
    console.error('Error during batch file reference cleanup:', error);
    return { 
      error: error.message || 'Unknown error during batch cleanup', 
      deleted: 0, 
      retained: 0 
    };
  }
}